import { TeamSide } from "../constants/teams";
import { GameFullResponse, LineUpPlayer } from "../types/gameFull";
import { GameRosters } from "../types/roster";

/**
 * Lineup management side panel (spec 1.5). Edits happen on a draft that is
 * saved when the panel closes (hooks/useScorekeepingActions saveLineups:
 * omnibus roster adds, then both teams' lineups saved whole).
 */

export type LineupDraftTeam = {
  rosterName: string;
  /** Ordered batting order */
  battingOrder: LineUpPlayer[];
  /** Full roster; the bench is the roster minus the batting order */
  roster: LineUpPlayer[];
};

export type LineupDraft = Record<TeamSide, LineupDraftTeam> & {
  /** Players of interest at this game who aren't on a roster */
  otherPlayers: LineUpPlayer[];
};

const hasPlayer = (players: LineUpPlayer[], playerId: string) =>
  players.some((p) => p.playerId === playerId);

/** Lineup entries with a batting order slot, in order. */
export const getBattingOrder = (lineUp: LineUpPlayer[]) =>
  lineUp
    .filter((p) => p.battingOrder != null)
    .sort((a, b) => (a.battingOrder ?? 0) - (b.battingOrder ?? 0));

export const createLineupDraft = (
  data: GameFullResponse,
  rosters: GameRosters,
): LineupDraft => {
  const team = (side: TeamSide): LineupDraftTeam => ({
    rosterName: rosters[side].rosterName,
    battingOrder: getBattingOrder(data.lineUp[side]),
    roster: rosters[side].players,
  });

  return {
    away: team("away"),
    home: team("home"),
    otherPlayers: data.lineUp.extra,
  };
};

/** Roster players who aren't in the batting order. */
export const getBench = (team: LineupDraftTeam) =>
  team.roster.filter((p) => !hasPlayer(team.battingOrder, p.playerId));

const updateTeam = (
  draft: LineupDraft,
  side: TeamSide,
  update: (team: LineupDraftTeam) => Partial<LineupDraftTeam>,
): LineupDraft => ({
  ...draft,
  [side]: { ...draft[side], ...update(draft[side]) },
});

export const reorderBattingOrder = (
  draft: LineupDraft,
  side: TeamSide,
  battingOrder: LineUpPlayer[],
) => updateTeam(draft, side, () => ({ battingOrder }));

/** Removes a player from the batting order (they go back to the bench). */
export const removeFromBattingOrder = (
  draft: LineupDraft,
  side: TeamSide,
  playerId: string,
) =>
  updateTeam(draft, side, (team) => ({
    battingOrder: team.battingOrder.filter((p) => p.playerId !== playerId),
  }));

/**
 * Adds players to the end of the batting order, in the given order. Anyone
 * not yet on the roster joins it (e.g. players of interest, new players),
 * and players of interest leave the "other players" list.
 */
export const addToBattingOrder = (
  draft: LineupDraft,
  side: TeamSide,
  players: LineUpPlayer[],
): LineupDraft => {
  const ids = new Set(players.map((p) => p.playerId));
  const next = updateTeam(draft, side, (team) => ({
    battingOrder: [
      ...team.battingOrder,
      ...players.filter((p) => !hasPlayer(team.battingOrder, p.playerId)),
    ],
    roster: [
      ...team.roster,
      ...players.filter((p) => !hasPlayer(team.roster, p.playerId)),
    ],
  }));
  return {
    ...next,
    otherPlayers: next.otherPlayers.filter((p) => !ids.has(p.playerId)),
  };
};

/**
 * Applies the draft: batting order slots are renumbered 1..n, and lineup
 * entries with a defensive position but no slot (e.g. a non-hitting pitcher)
 * are kept.
 */
export const applyLineupDraft = (
  data: GameFullResponse,
  /** The current rosters: their org and edit permission carry over */
  rosters: GameRosters,
  draft: LineupDraft,
): { data: GameFullResponse; rosters: GameRosters } => {
  const lineUpFor = (side: TeamSide): LineUpPlayer[] => {
    const order = draft[side].battingOrder.map((p, index) => ({
      ...p,
      battingOrder: index + 1,
    }));
    const fielders = data.lineUp[side]
      .filter((p) => p.position && !hasPlayer(order, p.playerId))
      .map(({ battingOrder: _slot, ...p }) => p);
    return [...order, ...fielders];
  };

  return {
    data: {
      ...data,
      lineUp: {
        away: lineUpFor("away"),
        home: lineUpFor("home"),
        extra: draft.otherPlayers,
      },
    },
    // Only the name and players come from the draft: rebuilding the roster
    // dropped its orgId and canEdit, so Add Players opened with no org
    rosters: {
      away: {
        ...rosters.away,
        rosterName: draft.away.rosterName,
        players: draft.away.roster,
      },
      home: {
        ...rosters.home,
        rosterName: draft.home.rosterName,
        players: draft.home.roster,
      },
    },
  };
};
